import type { ShopperPlatformContextConfig } from '@zillow/shopper-platform-config';

import type { getOmpDataQuery } from '../__generated__/graphql';
import { cookie } from '../constants/cookies';
import { NEW_YORK_ZIPS } from '../constants/newYorkZipCodes';
import env from '../env.factory';
import { createApolloClient, getApolloHeaders } from '../graphql/apolloUtils';
import { fetchHomepageGdpData } from '../graphql/fetchHomepageGdpData';
import { ompQuery } from '../graphql/query';
import { AllHopsPlacements } from '../types/genericTypes';
import { getHomepageSpecificAbs, isTreatmentForTrial } from '../utils/abs';
import { isZipInRegion } from '../utils/isZipInRegion';
import {
    getCentroidFromSearchCookie,
    getUserSearchContextFromSearchCookie,
} from '../utils/searchCookie';
import getSharedSessionState from '../utils/sharedSessionState';
import type { GlobalStoreState, Platforms } from './GlobalStoreProvider';

interface ApplicationStateProps {
    query?: { ipAddr?: string; forceNYFairHousingMsg?: string };
    headers: { [x: string]: string };
    cookies: { [x: string]: string };
}

/**
 * Assembles state
 * @param {ApplicationStateProps} req express request object
 * @returns {GlobalStoreState} initialState for app-level store
 */
export const getInitState = async (
    req: ApplicationStateProps,
    shopperPlatformConfig: ShopperPlatformContextConfig
): Promise<GlobalStoreState> => {
    const { abServiceConfig, intGraphqlTimeout, intGraphqlHost, s3sConfig, autocomplete } = env;
    const apolloClient = createApolloClient({
        apolloHeaders: getApolloHeaders({
            requestHeaders: req.headers,
            isLoggedIn: shopperPlatformConfig.viewer.isLoggedIn,
        }),
        uri: intGraphqlHost,
        timeout: intGraphqlTimeout,
    });
    // This line essentially determines the home recs that will display
    const ipAddr = req.headers['x-client-ip'] ?? req.query?.ipAddr ?? '';
    const platform: Platforms = shopperPlatformConfig.isMobile ? 'mbl' : 'web';
    const guid = req.headers['x-user-guid'] ?? '';
    const { deviceType } = shopperPlatformConfig;

    const homepageAbsPromise = getHomepageSpecificAbs(
        guid,
        abServiceConfig,
        shopperPlatformConfig.isMobile
    );
    const sharedSessionState = getSharedSessionState(guid, s3sConfig, { ipAddr });

    const gdpResponsePromise = fetchHomepageGdpData<getOmpDataQuery>({
        homePagesGraphqlQuery: ompQuery,
        apolloClient,
        variables: {
            isCarouselAdsRestricted: false,
            deviceType,
            /**
             * Add placements that are meant to render above the fold, i.e.: HomepageTopSlot
             * or placements that we want to show inmediately, i.e.: HomepageStickySlot
             */
            placements: [
                AllHopsPlacements.HOME_PAGE_HERO_IMAGE,
                AllHopsPlacements.HOME_PAGE_TOP_SLOT,
                AllHopsPlacements.HOME_PAGE_STICKY_SLOT,
            ],
        },
    });

    const { zipcode, city, centroid } = await sharedSessionState;
    const homepageAbs = await homepageAbsPromise;
    const gdpResponse = await gdpResponsePromise;

    const centroidFromSearchCookie = getCentroidFromSearchCookie(req.cookies[cookie.search] ?? '');
    const userSearchContextFromSearchCookie = getUserSearchContextFromSearchCookie(
        req.cookies[cookie.search] ?? ''
    );

    const isNLSUiUpdatesOnlyEnabled = isTreatmentForTrial({
        abs: homepageAbs,
        trial: 'SXP_NATURAL_LANGUAGE_SEARCH',
        treatment: 'UI_UPDATES_ONLY',
    });

    const isNLSDelayedSemanticAcEnabled = isTreatmentForTrial({
        abs: homepageAbs,
        trial: 'SXP_NATURAL_LANGUAGE_SEARCH',
        treatment: 'DELAYED_SEMANTIC_AC',
    });

    const isNLSEnabled = isTreatmentForTrial({
        abs: homepageAbs,
        trial: 'SXP_NATURAL_LANGUAGE_SEARCH',
        treatment: 'ON',
    });

    const isBuilderCommunityNameSearchEnabled = isTreatmentForTrial({
        abs: homepageAbs,
        trial: 'NC_WEB_COMMUNITY_NAME_SEARCH',
        treatment: 'ON',
    });

    const forceNYFairHousingMsg = req.query?.forceNYFairHousingMsg === 'true';
    // Show NY Fair Housing Message when in New York State
    const showNYFairHousingMsg = forceNYFairHousingMsg || isZipInRegion(zipcode, NEW_YORK_ZIPS);

    return {
        onsiteMessages: gdpResponse?.data.onsiteMessage ?? {},
        zipcode,
        city,
        abs: homepageAbs,
        centroid: centroidFromSearchCookie ?? centroid,
        userSearchContext: userSearchContextFromSearchCookie,
        platform,
        guid,
        isNLSEnabled,
        isNLSUiUpdatesOnlyEnabled,
        isNLSDelayedSemanticAcEnabled,
        showNYFairHousingMsg,
        autocompleteUrl: autocomplete.regionAddressAutocompleteURL,
        isBuilderCommunityNameSearchEnabled,
        deviceType,
        // TODO: This will be tied to an experiment flag in the future
        isJumpBackInEnabled: false,
    };
};
